import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Download,
  ExternalLink,
  Rocket,
  FolderOpen,
  Play,
  Cpu,
  HardDriveDownload,
  ShoppingCart,
  ShieldCheck,
} from "lucide-react";
import { fetchGzdoom, fetchFreedoom } from "@/lib/api";

const ZDOOM_URL = "https://zdoom.org/downloads";

const STEPS = [
  {
    icon: Download,
    title: "1. Download GZDoom",
    body: "Grab the latest stable GZDoom for Windows, macOS or Linux from the official downloads page. It's free and open source.",
  },
  {
    icon: FolderOpen,
    title: "2. Add your IWAD",
    body: "GZDoom needs a base game file (IWAD). Grab free Freedoom above, or use DOOM2.WAD from your bought copy. Put it next to GZDoom.",
  },
  {
    icon: Rocket,
    title: "3. Grab a WAD here",
    body: "Browse the catalog, download any mod, and note its launch command shown on the mod's Requirements tab.",
  },
  {
    icon: Play,
    title: "4. Launch & play",
    body: "Drag the .wad/.pk3 onto gzdoom.exe, or run the launch command in a terminal. RIP AND TEAR!",
  },
];

const PORTS = [
  { name: "GZDoom", color: "#22d3ee", desc: "The most popular modern port. Runs almost everything, incl. GZDoom-only mods." },
  { name: "Boom / PrBoom+", color: "#fbbf24", desc: "Lightweight & accurate for classic Boom-compatible megawads and demos." },
  { name: "Zandronum", color: "#a3e635", desc: "Built for online co-op & deathmatch with friends." },
  { name: "Eternity", color: "#c084fc", desc: "Advanced vanilla-plus port with unique mapping features." },
];

export const GetGzdoom = () => {
  const [gz, setGz] = useState(null);
  const [fd, setFd] = useState(null);

  useEffect(() => {
    fetchGzdoom().then(setGz).catch(() => {});
    fetchFreedoom().then(setFd).catch(() => {});
  }, []);

  const released = gz?.published_at
    ? new Date(gz.published_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div data-testid="gzdoom-page" className="max-w-5xl mx-auto">
      {/* Hero */}
      <motion.section
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-xl border border-[#2e2a36] grain scanlines bg-[#121115] p-8 sm:p-12 mb-10"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#06b6d4]/10 via-transparent to-[#ef4444]/10" />
        <div className="relative z-30 max-w-2xl">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 border border-[#06b6d4]/50 bg-[#06b6d4]/10 rounded-full">
            <Cpu className="w-3.5 h-3.5 text-[#06b6d4]" />
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#22d3ee]">
              New to Doom modding?
            </span>
          </div>
          {gz?.version && (
            <div
              data-testid="gzdoom-latest-version"
              className="inline-flex items-center gap-2 mb-4 ml-0 sm:ml-3 px-3 py-1.5 border border-[#84cc16]/50 bg-[#84cc16]/10 rounded-full"
            >
              <span className="w-2 h-2 rounded-full bg-[#84cc16] animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#a3e635]">
                Latest: v{gz.version}
                {released ? ` · ${released}` : ""}
              </span>
            </div>
          )}
          <h1 className="font-pixel text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-tight mb-4">
            Get GZDoom
          </h1>
          <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-7 font-body">
            To play the mods in this vault you need a <span className="text-[#84cc16]">source port</span> — a
            modern engine that runs classic Doom. GZDoom is the easiest place to start. Download it once, then
            play any WAD you find here.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              data-testid="gzdoom-download-btn"
              href={ZDOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 bg-[#ef4444] hover:bg-[#dc2626] text-white font-pixel text-xs uppercase tracking-wider px-6 py-3.5 rounded-md transition-all hover:shadow-[0_0_24px_rgba(239,68,68,0.5)]"
            >
              <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              Download GZDoom
            </a>
            <a
              data-testid="gzdoom-versions-link"
              href={ZDOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 border border-[#2e2a36] hover:border-[#06b6d4] text-gray-200 hover:text-[#22d3ee] font-mono text-xs uppercase tracking-wider px-5 py-3.5 rounded-md transition-colors bg-[#18161d]/60"
            >
              All Versions
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <p className="mt-4 font-mono text-[11px] text-gray-600">
            Official source: zdoom.org/downloads · version auto-checked weekly
          </p>
        </div>
      </motion.section>

      {/* IWAD — the base game file GZDoom needs */}
      <section data-testid="iwad-section" className="mb-12">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="font-pixel text-lg sm:text-xl text-white uppercase tracking-wide">
            Step 2 · Get a Game File (IWAD)
          </h2>
        </div>
        <p className="text-sm text-gray-400 font-body leading-relaxed mb-5 max-w-3xl">
          GZDoom is just the engine — it needs a base game file called an{" "}
          <span className="text-[#84cc16]">IWAD</span> to run. The original{" "}
          <span className="text-gray-200">DOOM2.WAD</span> is a commercial file we can't legally
          host, so grab the free, open-source <span className="text-gray-200">Freedoom</span> below
          (a drop-in replacement that runs Doom II mods), or buy the genuine Doom II.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Freedoom — free & legal direct download */}
          <div
            data-testid="iwad-freedoom-card"
            className="relative overflow-hidden rounded-lg border border-[#84cc16]/40 bg-[#121115] p-6"
          >
            <div className="absolute top-0 right-0 px-3 py-1 bg-[#84cc16] text-[#0a0a0c] font-mono text-[9px] tracking-widest uppercase rounded-bl-lg">
              Free · Legal
            </div>
            <div className="flex items-center gap-2 mb-3">
              <HardDriveDownload className="w-5 h-5 text-[#84cc16]" />
              <h3 className="font-pixel text-base text-white">Freedoom</h3>
              {fd?.version && (
                <span
                  data-testid="freedoom-version"
                  className="font-mono text-[10px] text-[#a3e635] border border-[#84cc16]/40 bg-[#84cc16]/10 rounded px-1.5 py-0.5"
                >
                  v{fd.version}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 font-body leading-relaxed mb-5">
              A complete, free IWAD built by the community. The download includes{" "}
              <span className="text-gray-300">freedoom2.wad</span> (the Doom II equivalent) and{" "}
              <span className="text-gray-300">freedoom1.wad</span> — unzip it next to GZDoom and
              you're ready to play almost any mod on Wad Drop.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                data-testid="freedoom-download-btn"
                href={fd?.download_url || "https://freedoom.github.io/download.html"}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 bg-[#84cc16] hover:bg-[#65a30d] text-[#0a0a0c] font-pixel text-xs uppercase tracking-wider px-5 py-3 rounded-md transition-all hover:shadow-[0_0_24px_rgba(132,204,22,0.4)]"
              >
                <Download className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
                Download Freedoom
              </a>
              <a
                data-testid="freedoom-site-link"
                href="https://freedoom.github.io"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-[#2e2a36] hover:border-[#84cc16] text-gray-300 hover:text-[#a3e635] font-mono text-xs uppercase tracking-wider px-4 py-3 rounded-md transition-colors"
              >
                About
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Original Doom II — legit purchase */}
          <div
            data-testid="iwad-original-card"
            className="rounded-lg border border-[#2e2a36] bg-[#121115] p-6"
          >
            <div className="flex items-center gap-2 mb-3">
              <ShoppingCart className="w-5 h-5 text-[#f59e0b]" />
              <h3 className="font-pixel text-base text-white">Original Doom II</h3>
            </div>
            <p className="text-xs text-gray-400 font-body leading-relaxed mb-2">
              Want the real <span className="text-gray-300">DOOM2.WAD</span> with id's original art
              and music? Buy Doom II — you'll find DOOM2.WAD in the game's install folder, fully
              legal to use with GZDoom.
            </p>
            <p className="flex items-center gap-1.5 text-[11px] font-mono text-[#84cc16] mb-5">
              <ShieldCheck className="w-3.5 h-3.5" />
              Supports the original developers
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                data-testid="buy-steam-link"
                href="https://store.steampowered.com/app/2300/DOOM_II/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#2e2a36] hover:border-[#f59e0b] text-gray-200 hover:text-[#fbbf24] font-mono text-xs uppercase tracking-wider px-5 py-3 rounded-md transition-colors"
              >
                Buy on Steam
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <a
                data-testid="buy-gog-link"
                href="https://www.gog.com/game/doom_ii_final_doom"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#2e2a36] hover:border-[#f59e0b] text-gray-200 hover:text-[#fbbf24] font-mono text-xs uppercase tracking-wider px-5 py-3 rounded-md transition-colors"
              >
                Buy on GOG
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <h2 className="font-pixel text-lg sm:text-xl text-white uppercase tracking-wide mb-5">
        Quick Start
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.06 }}
              data-testid={`gzdoom-step-${i + 1}`}
              className="flex gap-4 bg-[#18161d] border border-[#2e2a36] rounded-lg p-5"
            >
              <div className="shrink-0 w-10 h-10 rounded-md bg-[#0a0a0c] border border-[#2e2a36] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#84cc16]" />
              </div>
              <div>
                <h3 className="font-pixel text-sm text-white mb-1.5">{s.title}</h3>
                <p className="text-xs text-gray-400 font-body leading-relaxed">{s.body}</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Ports */}
      <h2 className="font-pixel text-lg sm:text-xl text-white uppercase tracking-wide mb-5">
        Other Source Ports
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
        {PORTS.map((p) => (
          <div
            key={p.name}
            data-testid={`gzdoom-port-${p.name.toLowerCase().replace(/[^a-z]/g, "")}`}
            className="bg-[#121115] border border-[#2e2a36] rounded-lg p-5"
          >
            <span
              className="inline-block mb-2 px-2.5 py-1 rounded font-mono text-[10px] tracking-wider uppercase border"
              style={{ background: `${p.color}22`, borderColor: p.color, color: p.color }}
            >
              {p.name}
            </span>
            <p className="text-xs text-gray-400 font-body leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
